import { useAuth } from '@clerk/clerk-react';
import { CheckSquare, Download } from 'lucide-react';
import { useMemo, useState } from 'react';
import { API_BASE_URL } from '../api/client.js';
import { useMe } from '../api/useMe.js';
import { useCheckInReport } from '../api/useReports.js';
import { useTeamsList } from '../api/useTeams.js';
import { EmptyState } from '../components/EmptyState.js';
import { cn } from '../lib/cn.js';

const DAY_MS = 24 * 60 * 60_000;

function toDateInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function dateToWindowIso(dateStr: string, endOfDay: boolean): string {
  const d = new Date(dateStr);
  if (endOfDay) d.setHours(23, 59, 59, 999);
  return d.toISOString();
}

export default function CheckInReport() {
  const me = useMe();
  const teams = useTeamsList();
  const allTeams = teams.data?.teams ?? [];
  const today = new Date();
  const twoWeeksAgo = new Date(today.getTime() - 14 * DAY_MS);
  const [fromDate, setFromDate] = useState(toDateInputValue(twoWeeksAgo));
  const [toDate, setToDate] = useState(toDateInputValue(today));
  const [teamId, setTeamId] = useState<string>('');
  const fromIso = useMemo(() => dateToWindowIso(fromDate, false), [fromDate]);
  const toIso = useMemo(() => dateToWindowIso(toDate, true), [toDate]);
  const report = useCheckInReport(
    { from: fromIso, to: toIso, teamIds: teamId || undefined },
    Boolean(me.data),
  );
  const rows = report.data?.rows ?? [];

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-5 flex items-end justify-between">
        <div>
          <span className="eyebrow">Reports</span>
          <h1 className="mt-1 font-heading text-[22px] font-bold tracking-tightest text-ink">
            Check-in compliance
          </h1>
          <p className="text-sm text-ink-faint">
            Per-worker check-in totals. Compliance % = completed / (completed + overdue); cancelled
            check-ins are excluded from the denominator.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <Picker label="Team" value={teamId} onChange={setTeamId}>
            <option value="">
              {me.data?.capabilities?.monitor || me.data?.capabilities?.admin
                ? 'All teams'
                : 'My teams'}
            </option>
            {allTeams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Picker>
          <DateField label="From" value={fromDate} onChange={setFromDate} />
          <DateField label="To" value={toDate} onChange={setToDate} />
          <CsvDownloadButton
            path="check-ins"
            from={fromIso}
            to={toIso}
            teamIds={teamId || undefined}
          />
        </div>
      </div>

      {report.isError && (
        <Banner kind="error">Failed to load report: {(report.error as Error).message}</Banner>
      )}
      {report.isLoading && (
        <div className="panel p-8 text-center text-sm text-ink-faint">Loading…</div>
      )}
      {!report.isLoading && rows.length === 0 && !report.isError && (
        <EmptyState
          icon={CheckSquare}
          title="No check-ins in this window"
          description="No workers in scope started any check-ins for these dates. Try a wider window or a different team."
        />
      )}

      {rows.length > 0 && (
        <div className="panel">
          <table className="min-w-full divide-y divide-line">
            <thead className="bg-surface-3">
              <tr>
                <Th>Worker</Th>
                <Th className="text-right">Started</Th>
                <Th className="text-right">Completed</Th>
                <Th className="text-right">Overdue</Th>
                <Th className="text-right">Cancelled</Th>
                <Th className="text-right">Avg duration</Th>
                <Th className="text-right">Compliance</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line bg-white">
              {rows.map((r) => (
                <tr key={r.user_id} className="hover:bg-surface-3">
                  <Td>
                    <div className="text-sm font-medium text-ink">{r.display_name}</div>
                    {r.job_title && <div className="text-xs text-ink-faint">{r.job_title}</div>}
                  </Td>
                  <Td className="text-right tabular-nums">{r.started_count}</Td>
                  <Td className="text-right tabular-nums text-emerald-700">{r.completed_count}</Td>
                  <Td className="text-right tabular-nums text-red-700">{r.overdue_count}</Td>
                  <Td className="text-right tabular-nums text-ink-mute">{r.cancelled_count}</Td>
                  <Td className="text-right tabular-nums text-ink-dim">
                    {r.avg_duration_minutes} min
                  </Td>
                  <Td
                    className={cn(
                      'text-right tabular-nums font-semibold',
                      r.compliance_percent === -1
                        ? 'text-ink-mute'
                        : r.compliance_percent >= 90
                          ? 'text-emerald-700'
                          : r.compliance_percent >= 70
                            ? 'text-amber-700'
                            : 'text-red-700',
                    )}
                  >
                    {r.compliance_percent === -1 ? '—' : `${r.compliance_percent}%`}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Picker({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-[11px] font-medium text-ink-faint">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-w-[12rem] rounded-md border border-line-strong bg-white px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
      >
        {children}
      </select>
    </div>
  );
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="mb-1 block text-[11px] font-medium text-ink-faint">{label}</label>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-line-strong bg-white px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
      />
    </div>
  );
}

function Banner({ kind, children }: { kind: 'error'; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'mb-4 rounded-md border px-4 py-3 text-sm',
        kind === 'error' && 'border-red-200 bg-red-50 text-red-800',
      )}
    >
      {children}
    </div>
  );
}

function CsvDownloadButton({
  path,
  from,
  to,
  teamIds,
}: {
  path: string;
  from: string;
  to: string;
  teamIds?: string;
}) {
  const { getToken } = useAuth();
  const [busy, setBusy] = useState(false);
  async function onClick() {
    setBusy(true);
    try {
      const token = await getToken();
      const sp = new URLSearchParams({ from, to });
      if (teamIds) sp.set('team_ids', teamIds);
      const res = await fetch(`${API_BASE_URL}/reports/${path}.csv?${sp.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${path}-${from.slice(0, 10)}-to-${to.slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setBusy(false);
    }
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="inline-flex h-9 items-center gap-1.5 rounded-md border border-line-strong bg-white px-3 text-sm font-medium text-ink-dim transition hover:bg-surface-3 disabled:opacity-50"
      title="Download as CSV"
    >
      <Download className="h-3.5 w-3.5" />
      {busy ? 'Preparing…' : 'CSV'}
    </button>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn(
        'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-ink-faint',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-4 py-3 text-sm', className)}>{children}</td>;
}
